import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import type { Engineer, EngineerState } from '../api/types'
import { useEngineers } from '../hooks/useApi'
import { tokens } from '../theme'

const stateOrder: EngineerState[] = ['overdue_checkin', 'offroute', 'active', 'idle', 'break', 'offline']

export function EngineerRail() {
  const { data, isLoading, isError, error } = useEngineers()

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <CircularProgress size={20} />
      </Paper>
    )
  }

  if (isError) {
    return (
      <Alert severity="error" variant="outlined">
        Failed to load engineers: {error instanceof Error ? error.message : 'unknown'}
      </Alert>
    )
  }

  const engineers = data ?? []
  const grouped = groupByState(engineers)

  return (
    <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Engineers
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {engineers.length} on roster
        </Typography>
      </Box>

      <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
        {stateOrder.map((state) => {
          const list = grouped[state] ?? []
          if (list.length === 0) return null
          return (
            <Box key={state} sx={{ p: 1.5, bgcolor: state === 'overdue_checkin' ? 'rgba(229,80,28,0.05)' : 'transparent' }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: stateColour(state),
                  }}
                />
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  {stateLabel(state)} · {list.length}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {list.map((e) => (
                  <Box key={e.id}>
                    <Typography variant="body2" sx={{ fontSize: 12.5, lineHeight: 1.3 }}>
                      {e.display_name}{' '}
                      <Box component="span" sx={{ color: 'text.secondary', fontSize: 11 }}>
                        · {e.external_ref}
                        {e.home_patch ? ` · ${e.home_patch}` : ''}
                      </Box>
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10.5 }}>
                      {formatShift(e)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )
        })}
      </Stack>
    </Paper>
  )
}

function groupByState(engineers: Engineer[]): Partial<Record<EngineerState, Engineer[]>> {
  const out: Partial<Record<EngineerState, Engineer[]>> = {}
  for (const e of engineers) {
    const bucket = out[e.state] ?? []
    bucket.push(e)
    out[e.state] = bucket
  }
  return out
}

function stateColour(state: EngineerState): string {
  switch (state) {
    case 'active':
      return '#22C55E'
    case 'idle':
      return '#94A3B8'
    case 'break':
      return '#A855F7'
    case 'offroute':
      return tokens.alert
    case 'overdue_checkin':
      return tokens.alert
    case 'offline':
      return tokens.textSec
  }
}

function stateLabel(state: EngineerState): string {
  if (state === 'overdue_checkin') return 'Check-in overdue'
  return state.charAt(0).toUpperCase() + state.slice(1)
}

// formatShift renders "Shift 07:30–16:30 · attended (30m overtime)" or a
// clear "off today" when nothing's scheduled. Times are the planned
// slot; actual behaviour is folded into the status suffix so the row
// stays one line for scanning.
function formatShift(e: Engineer): string {
  if (!e.shift_planned_start || !e.shift_planned_end) return 'Off today'
  const start = timeOnly(e.shift_planned_start)
  const end = timeOnly(e.shift_planned_end)
  const status: string = e.shift_status ?? 'planned'
  let suffix = status
  if (status === 'attended' && e.shift_actual_end && e.shift_planned_end) {
    const otMin = Math.round((new Date(e.shift_actual_end).getTime() - new Date(e.shift_planned_end).getTime()) / 60000)
    if (otMin > 5) suffix = `attended (+${otMin}m overtime)`
  }
  return `Shift ${start}–${end} · ${suffix}`
}

function timeOnly(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
  } catch {
    return '—'
  }
}
